import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { profileModel, shopModel, kioskModel } from '../Axios/AxiosConfig';
import { toast, Zoom } from "react-toastify";
import MedicineRecommendations from '../AI Suggestions/MedicineRecommendation';
import { useNavigate, useNavigation } from 'react-router-dom';
import { ayurvedaAssessments } from '../Appointments/ayurvedaAssessments';

function cleanJsonString(rawString) {
    return rawString
        .replace(/```json/gi, '')
        .replace(/```/g, '')
        .trim();
}

const ayurvedaFields = {
    Agni: {
        options: ["Mandagni", "Vishamagni", "Samagni", "Tikshnagni"]
    },
    Manas: {
        options: ["Pravara", "Madhyama", "Avara"]
    },
    Nidra: {
        options: ["Normal", "Alpa", "Atinidra"]
    },
    Koshtam: {

        options: ["Mrdu", "Madhyama", "Krura"]
    },
    Malam: {

        options: ["Ama-avastha", "Nirama-avastha"]
    },
    Mutram: {

        options: ["Vata", "Pitta", "Kapha"]
    },
    Vyayamam: {

        options: ["Uttama", "Madhyama", "Alpa"]
    },
    Vyadhiavastha: {

        options: ["Asukari", "Chirakari"]
    }
};

const getPreferredOrder = (param) => ayurvedaFields?.[param]?.options ?? [];

const majorityFromQuestions = (param, questions = []) => {
    const counts = new Map();
    const firstSeenOrder = [];

    questions.forEach(q => {
        const v = q?.answer ?? q?.selected ?? q?.value;
        if (!v) return;
        if (!counts.has(v)) {
            counts.set(v, 0);
            firstSeenOrder.push(v);
        }
        counts.set(v, counts.get(v) + 1);
    });

    if (counts.size === 0) return ""; // nothing selected

    let max = -1;
    for (const c of counts.values()) max = Math.max(max, c);
    const candidates = [...counts.entries()].filter(([, c]) => c === max).map(([v]) => v);

    if (candidates.length === 1) return candidates[0];

    // tie-break by ayurvedaFields order, else first-seen
    const pref = getPreferredOrder(param);
    if (pref.length) {
        for (const opt of pref) if (candidates.includes(opt)) return opt;
    }
    for (const v of firstSeenOrder) if (candidates.includes(v)) return v;
    return candidates[0];
};

const usePrescriptionStore = create(
    persist(
        (set, get) => ({
            selectedPatientdetails: null,
            selectedAppointemntdetails: null,
            audioBase64: null,
            selectedPrescription: null,
            isFollowUpEdit: false,

            setSelectedPatientdetails: (dataOrUpdater) =>
                set((state) => ({
                    selectedPatientdetails:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state?.selectedPatientdetails)
                            : dataOrUpdater,
                })),

            setSelectedAppointemntdetails: (data) => set({ selectedAppointemntdetails: data }),
            setAudioBase64: (data) => set({ audioBase64: data }),
            setPrescription: (data) => set({ selectedPrescription: data }),
            setIsFollowUpEdit: (val) => set({ isFollowUpEdit: val }),

            /* ─── Auto-populate all form fields from a saved prescription ─── */
            populateFromPrescription: (data) => {
                if (!data) return;

                const isFollowUp = get().isFollowUpEdit;

                // Helper: check if a value is "filled"
                const isFilled = (v) => {
                    if (v === null || v === undefined || v === '') return false;
                    if (Array.isArray(v)) return v.length > 0;
                    if (typeof v === 'object') return Object.values(v).some(isFilled);
                    return true;
                };

                // Build the state update
                const stateUpdate = {};

                // ── Simple text / object fields ──
                if (data.Medicaldetails) stateUpdate.Medicaldetails = data.Medicaldetails;
                if (data.symptoms) stateUpdate.symptoms = data.symptoms;
                if (data.medicalHistory) stateUpdate.medicalHistory = data.medicalHistory;
                if (data.surgicalHistory) stateUpdate.surgicalHistory = data.surgicalHistory;
                if (data.familyHistory) stateUpdate.familyHistory = data.familyHistory;
                if (data.obgynHistory) stateUpdate.obgynHistory = data.obgynHistory;
                if (data.socioEconomicStatus) stateUpdate.socioEconomicStatus = data.socioEconomicStatus;
                if (data.metrics) stateUpdate.metrics = data.metrics;
                if (data.bodyComposition) stateUpdate.bodyComposition = data.bodyComposition;
                if (data.customMetrics) stateUpdate.customMetrics = data.customMetrics;
                if (data.labTests) stateUpdate.labTests = data.labTests;
                if (data.FoodtobeTaken) stateUpdate.FoodtobeTaken = data.FoodtobeTaken;
                if (data.Foodtobeavoided) stateUpdate.Foodtobeavoided = data.Foodtobeavoided;
                if (data.selectedRecipes) stateUpdate.selectedRecipes = data.selectedRecipes;
                if (data.Medicineinputs) stateUpdate.Medicineinputs = data.Medicineinputs;
                if (data.currentDrugs) stateUpdate.currentDrugs = data.currentDrugs;
                if (data.Ayurvedaparameters) stateUpdate.Ayurvedaparameters = data.Ayurvedaparameters;
                if (data.yogalist) stateUpdate.yogalist = data.yogalist;
                if (data.pranayama) stateUpdate.pranayama = data.pranayama;
                if (data.meditationList) stateUpdate.meditationList = data.meditationList;
                if (data.Dosham) stateUpdate.Dosham = data.Dosham;
                if (data.Prakriti_details) stateUpdate.Prakriti_details = data.Prakriti_details;
                if (data.doshaAIdiagnois) stateUpdate.doshaAIdiagnois = data.doshaAIdiagnois;
                if (data.tcmPulseDiagnosis) stateUpdate.tcmPulseDiagnosis = data.tcmPulseDiagnosis;
                if (data.associateComplaints) stateUpdate.associateComplaints = data.associateComplaints;
                if (data.painHistory) stateUpdate.painHistory = data.painHistory;
                if (data.painTypeOptions) stateUpdate.painTypeOptions = data.painTypeOptions;
                if (data.painActivityEffects) stateUpdate.painActivityEffects = data.painActivityEffects;
                if (data.generalExamination) stateUpdate.generalExamination = data.generalExamination;
                if (data.localExamination) stateUpdate.localExamination = data.localExamination;
                if (data.marmaPoints) stateUpdate.marmaPoints = data.marmaPoints;
                if (data.otherJoints) stateUpdate.otherJoints = data.otherJoints;
                if (data.followUpDetails) stateUpdate.followUpDetails = data.followUpDetails;
                if (data.aiParameters) stateUpdate.aiParameters = data.aiParameters;

                // ── String fields ──
                if (data.dosha !== undefined) stateUpdate.dosha = data.dosha;
                if (data.dhatu !== undefined) stateUpdate.dhatu = data.dhatu;
                if (data.chakra !== undefined) stateUpdate.chakra = data.chakra;
                if (data.kosha !== undefined) stateUpdate.kosha = data.kosha;
                if (data.FollowUp !== undefined) stateUpdate.FollowUp = data.FollowUp;
                if (data.lifestylechanges !== undefined) stateUpdate.lifestylechanges = data.lifestylechanges;
                if (data.treatmentPlan !== undefined) stateUpdate.treatmentPlan = data.treatmentPlan;
                if (data.homeProgram !== undefined) stateUpdate.homeProgram = data.homeProgram;
                if (data.ergonomicsAdvice !== undefined) stateUpdate.ergonomicsAdvice = data.ergonomicsAdvice;
                if (data.rangeOfMotion !== undefined) stateUpdate.rangeOfMotion = data.rangeOfMotion;
                if (data.labInvestigation !== undefined) stateUpdate.labInvestigation = data.labInvestigation;
                if (data.irisDiagnosis !== undefined) stateUpdate.irisDiagnosis = data.irisDiagnosis;
                if (data.formData) stateUpdate.formData = data.formData;
                if (data.diseases) stateUpdate.diseases = data.diseases;
                if (data.lifestylePractices) stateUpdate.lifestylePractices = data.lifestylePractices;

                // ── Auto-open CollapsibleSections that have data ──
                const sectionFieldMap = {
                    'Chief Complaints': [data.symptoms, data.Medicaldetails?.Complaints],
                    'Vital Signs': [data.metrics],
                    'Body Composition': [data.bodyComposition],
                    'AI Reports': [data.aiParameters],
                    'Additional Metrics': [data.customMetrics],
                    'Suggested Lab Tests': [data.labTests],
                    'Medical History': [data.medicalHistory],
                    'Surgical History': [data.surgicalHistory],
                    'Family History': [data.familyHistory],
                    'Socio-Economical History': [data.socioEconomicStatus],
                    'Dietary Recommendations': [data.FoodtobeTaken, data.Foodtobeavoided],
                    'Recipes': [data.selectedRecipes],
                    'Lifestyle Practices': [data.lifestylePractices],
                    'Treatment Plan': [data.treatmentPlan],
                    'Home Program': [data.homeProgram],
                    'Ergonomics Advice': [data.ergonomicsAdvice],
                    'Dosha': [data.dosha],
                    'Prakriti Dosha': [data.Prakriti_details],
                    'Vikriti Dosha': [data.Dosham],
                    'Dhatu': [data.dhatu],
                    'Chakra': [data.chakra],
                    'Marma / Varma': [data.marmaPoints],
                    'Kosha': [data.kosha],
                    'Lifestyle Changes': [data.lifestylechanges],
                    'Follow up': [data.FollowUp, data.followUpDetails],
                };

                const openSections = {};
                for (const [sectionTitle, fields] of Object.entries(sectionFieldMap)) {
                    if (fields.some(isFilled)) {
                        openSections[sectionTitle] = true;
                    }
                }

                stateUpdate.collapsedSections = {
                    ...get().collapsedSections,
                    ...openSections,
                };

                set(stateUpdate);
            },

            // Collapsible section state – keyed by section title
            collapsedSections: {},

            toggleSection: (sectionKey) =>
                set((state) => ({
                    collapsedSections: {
                        ...state.collapsedSections,
                        [sectionKey]: !state.collapsedSections[sectionKey],
                    },
                })),

            setSectionOpen: (sectionKey, isOpen) =>
                set((state) => ({
                    collapsedSections: {
                        ...state.collapsedSections,
                        [sectionKey]: isOpen,
                    },
                })),

            openRecipeAI: false,
            recipeLoading: false,
            recipeAIRecommendation: [],
            selectedRecipes: [],

            familyHistory: [
                { relation: 'Father', disease: '', duration: '' },
                { relation: 'Mother', disease: '', duration: '' }
            ],

            metrics: {
                bloodPressure: "",
                randomBloodSugar: "",
                pulseRate: "",
                oximeterValue: "",
                gripStrength: "",
            },

            bodyComposition: {
                weight: "",
                bmi: "",
                bodyFat: "",
                visceralFat: "",
                muscleMass: "",
                boneMass: "",
                water: "",
                protein: "",
                bmr: "",
                metabolicAge: "",
            },

            setBodyComposition: (dataOrUpdater) =>
                set((state) => ({
                    bodyComposition:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.bodyComposition)
                            : { ...state.bodyComposition, ...dataOrUpdater },
                })),

            formData: {},

            customMetrics: [],
            isEditingResult: null,  // holds parameter name or null
            editValue: "",

            patientProfile: null,
            herbalProducts: [],
            AllDiet: {},
            AllrawDiet: [],
            AllRecipes: [],
            Recipes: [],
            Practices: [],
            transcript: "",
            error: null,
            includedCategories: [],
            avoidedCategories: [],
            creationStartTime: null,
            lifestylePracticesLoading: false,
            MedicineRecommendationLoading: false,

            labTests: [],
            FoodtobeTaken: [],
            Foodtobeavoided: [],
            yogalist: {},
            meditationList: {},
            pranayama: {},
            lifestylechanges: "",
            FollowUp: "No",
            symptoms: [],

            associateComplaints: [
                {
                    category: "Digestive Disorders",
                    dhatu: "Rasa",
                    symptoms: [
                        { symptoms: "Indigestion", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "IBS", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Acidity", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Gastritis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Ulcer", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "GERD", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
                {
                    category: "Hematological Disorders",
                    dhatu: "Rakta",
                    symptoms: [
                        { symptoms: "Anemia", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Cholesterol", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Hypertension", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Triglyceride", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Varicose vein", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
                {
                    category: "Muscular Disorders",
                    dhatu: "Mamsa",
                    symptoms: [
                        { symptoms: "Obesity", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Muscle pain", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Weight Loss", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Debility", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
                {
                    category: "Skin Disorders",
                    dhatu: "Medas",
                    symptoms: [
                        { symptoms: "Itching", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Vitiligo", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Discolouration", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Eczema", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Psoriasis", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
                {
                    category: "Skeletal Disorders",
                    dhatu: "Asthi",
                    symptoms: [
                        { symptoms: "Osteo Arthritis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Disc prolapse", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Sciatica", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Rheumatoid arthritis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Lumbar Spondylosis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Cervical Spondylosis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Gout", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
                {
                    category: "Nervous Disorders",
                    dhatu: "Majja",
                    symptoms: [
                        { symptoms: "Parkinson's disease", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Alzheimer's disease", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Paralysis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Immunity", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
                {
                    category: "Anorectal Disorders",
                    dhatu: "Mooladhara",
                    symptoms: [
                        { symptoms: "Piles", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Fissure in ano", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Fistula in ano", severity: 0, duration: "", frequency: "", goal: false },

                    ],
                },
                {
                    category: "Reproductive System Disorders",
                    dhatu: "Swadhisthana",
                    symptoms: [
                        { symptoms: "Male Infertility", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Prostate Disorder", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Erectile dysfunction", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Female infertility", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Fibroid", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Amenorrhoea", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Irregular menses", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Leucorrhoea", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Dysmenorrhoea", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "PCOD", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Endometriosis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Monopausal Syndrome", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
                {
                    category: "Kidney Disorders",
                    dhatu: "Manipura",
                    symptoms: [
                        { symptoms: "Kidney stone", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Diabetes Mellitus", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Kidney failure", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Diabetes neuritis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Diabetes nephritis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Diabetic foot ulcer", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
                {
                    category: "Cardiac / Hepatic Disorders",
                    dhatu: "Anahata",
                    symptoms: [
                        { symptoms: "Angina Pectoris", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Cardiomyopathy", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Liver Cirrhosis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Fatty liver", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Coronary Artery diseases", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Atherosclerosis", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
                {
                    category: "Neck & thoracic related Disorders",
                    dhatu: "Vishuddhi",
                    symptoms: [
                        { symptoms: "Thyroid", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Asthma", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Wheezing", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
                {
                    category: "Head & ENT Disorders",
                    dhatu: "Ajna",
                    symptoms: [
                        { symptoms: "ENT", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Anxiety", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Hair Loss", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Sinusitis", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Stress", severity: 0, duration: "", frequency: "", goal: false },
                        { symptoms: "Migraine", severity: 0, duration: "", frequency: "", goal: false },
                    ],
                },
            ],

            medicalHistory: [
                { condition: "Hypertension", hasHistory: false, duration: "" },
                { condition: "Diabetes", hasHistory: false, duration: "" },
                { condition: "Cardiac Issues", hasHistory: false, duration: "" },
                { condition: "Thyroid", hasHistory: false, duration: "" }
            ],

            painHistory: {
                pattern: "",
                siteAndSide: "",
                onset: "",
                typeOfPain: "",
                irritability: "",
                rhythms: "",
                aggravatingFactors: "",
                relievingFactors: "",
                painScore: "",
                effectOnActivity: ""
            },

            painTypeOptions: [
                { label: "Prick", selected: false, duration: "" },
                { label: "Dull Ache", selected: false, duration: "" },
                { label: "Burning", selected: false, duration: "" },
                { label: "Throbbing", selected: false, duration: "" },
                { label: "Pulling", selected: false, duration: "" },
                { label: "Sharp Shooting", selected: false, duration: "" }
            ],

            painActivityEffects: [
                { activity: "Getting in/out of Bed", affected: false, duration: "" },
                { activity: "Walking up / down stairs", affected: false, duration: "" },
                { activity: "Getting in/out of chair", affected: false, duration: "" },
                { activity: "Standing / Walking", affected: false, duration: "" },
                { activity: "Working activities", affected: false, duration: "" },
                { activity: "Other activities (sitting, cooking, cleaning, lifting etc.)", affected: false, duration: "" },
                { activity: "Dysmenorrhoea", affected: false, duration: "" }
            ],

            generalExamination: {
                generalBodyBuilt: "",
                gait: "",
                posture: "",
                duration: "",
            },

            localExamination: {
                temperature: "",
                spasm: "",
                tenderness: "",
                otherFindings: "",
                duration: "",
                swelling: "",
                crepitus: ""
            },

            rangeOfMotion: "",

            labInvestigation: "",

            otherJoints: {
                tightness: null,
                contracture: null,
                deformity: null,
            },

            marmaPoints: [
                {
                    point: "",
                    location: "",
                    painLevel: 0,
                },
            ],

            kosha: "",

            irisDiagnosis: "",

            setIrisDiagnosis: (value) => set({ irisDiagnosis: value }),

            setKosha: (value) => set({ kosha: value }),

            setMarmaPoints: (points) => set({ marmaPoints: points }),

            setLabInvestigation: (value) =>
                set({ labInvestigation: value }),

            treatmentPlan: "",

            setTreatmentPlan: (value) =>
                set(() => ({
                    treatmentPlan: value,
                })),

            homeProgram: "",

            setHomeProgram: (value) =>
                set(() => ({
                    homeProgram: value,
                })),

            ergonomicsAdvice: "",

            setErgonomicsAdvice: (value) =>
                set(() => ({
                    ergonomicsAdvice: value,
                })),



            setAssociateComplaints: (value) =>
                set(() => ({
                    associateComplaints: value,
                })),

            followUpDetails: {
                date: "",
                followUp: "",
                remarks: "",
            },

            dosha: "",

            setDosha: (value) =>
                set(() => ({
                    dosha: value,
                })),

            dhatu: "",

            setDhatu: (value) =>
                set(() => ({
                    dhatu: value,
                })),

            chakra: "",

            setChakra: (value) =>
                set(() => ({
                    chakra: value,
                })),

            setFollowUpDetails: (field, value) =>
                set((state) => ({
                    followUpDetails: {
                        ...state.followUpDetails,
                        [field]: value,
                    },
                })),

            setOtherJoints: (key, value) =>
                set((state) => ({
                    otherJoints: {
                        ...state.otherJoints,
                        [key]: value,
                    },
                })),


            setRangeOfMotion: (value) =>
                set(() => ({
                    rangeOfMotion: value,
                })),


            setLocalExamination: (key, value) =>
                set((state) => ({
                    localExamination: {
                        ...state.localExamination,
                        [key]: value,
                    },
                })),

            setGeneralExamination: (key, value) =>
                set((state) => ({
                    generalExamination: {
                        ...state.generalExamination,
                        [key]: value,
                    },
                })),


            setPainActivityEffect: (index, field, value) => {
                const updated = [...get().painActivityEffects];
                updated[index][field] = value;
                set({ painActivityEffects: updated });
            },


            surgicalHistory: [],

            socioEconomicStatus: { status: "" },

            setSocioEconomicStatus: (value) =>
                set({ socioEconomicStatus: { status: value } }),

            setPainHistory: (field, value) =>
                set((state) => ({
                    painHistory: {
                        ...state.painHistory,
                        [field]: value,
                    },
                })),

            setPainTypeOption: (index, field, value) => {
                const updated = [...get().painTypeOptions];
                updated[index][field] = value;
                set({ painTypeOptions: updated });
            },

            addPainTypeOption: () => {
                const updated = [...get().painTypeOptions, { label: "", selected: false }];
                set({ painTypeOptions: updated });
            },

            obgynHistory: {
                lmp: "",
                menstrualCycle: "",
                menstrualFlow: "",
                dysmenorrhoea: null,
                leucorrhoea: null,
                delivery: ""
            },

            setObgynHistory: (field, value) =>
                set((state) => ({
                    obgynHistory: {
                        ...state.obgynHistory,
                        [field]: value,
                    },
                })),

            addSurgicalHistory: () => {
                const updated = [...get().surgicalHistory, { surgery: "", duration: "" }];
                set({ surgicalHistory: updated });
            },

            updateSurgicalHistory: (index, field, value) => {
                const updated = [...get().surgicalHistory];
                updated[index][field] = value;
                set({ surgicalHistory: updated });
            },

            deleteSurgicalHistory: (index) => {
                const updated = [...get().surgicalHistory];
                updated.splice(index, 1);
                set({ surgicalHistory: updated });
            },

            tcmPulseDiagnosis: {
                left: {
                    heart: "", liver: "", kidney: "", urinaryBladder: "", smallIntestine: "", gallBladder: ""
                },
                right: {
                    lung: "", spleen: "", stomach: "", kidney: "", largeIntestine: "", urinaryBladder: ""
                }
            },

            setTcmPulseDiagnosis: (hand, organ, value) =>
                set((state) => ({
                    tcmPulseDiagnosis: {
                        ...state.tcmPulseDiagnosis,
                        [hand]: {
                            ...state.tcmPulseDiagnosis[hand],
                            [organ]: value,
                        },
                    },
                })),


                        diseases: [],
                        complications: [],
                        habits: [],

            lifestylePractices: [],

            Prakriti_details: null,

            lifestylePracticesLoading: false,

            medicinerecommendation: [],

            MedicineRecommendationLoading: false,

            aiParameters: [],

            doshaAIdiagnois: null,

            Dosham: {
                Increased: {
                    doshas: [],
                    percentage: {},
                    intensity: {},
                },
                Decreased: {
                    doshas: [],
                    percentage: {},
                    intensity: {},
                },
            },

                        diseases: [],
                        complications: [],
                        habits: [],

            lifestylePractices: [],

            Prakriti_details: null,

            lifestylePracticesLoading: false,

            medicinerecommendation: [],

            MedicineRecommendationLoading: false,

            aiParameters: [],

            doshaAIdiagnois: null,

            Dosham: {
                Increased: {
                    doshas: [],
                    percentage: {},
                    intensity: {},
                },
                Decreased: {
                    doshas: [],
                    percentage: {},
                    intensity: {},
                },
            },

                        diseases: [],
                        complications: [],
                        habits: [],

            lifestylePractices: [],

            Prakriti_details: null,

            lifestylePracticesLoading: false,

            medicinerecommendation: [],

            MedicineRecommendationLoading: false,

            aiParameters: [],

            doshaAIdiagnois: null,

            Dosham: {
                Increased: {
                    doshas: [],
                    percentage: {},
                    intensity: {},
                },
                Decreased: {
                    doshas: [],
                    percentage: {},
                    intensity: {},
                },
            },

                        diseases: [],
                        complications: [],
                        habits: [],

            lifestylePractices: [],

            Prakriti_details: null,

            lifestylePracticesLoading: false,

            medicinerecommendation: [],

            MedicineRecommendationLoading: false,

            aiParameters: [],

            doshaAIdiagnois: null,

            Dosham: {
                Increased: {
                    doshas: [],
                    percentage: {},
                    intensity: {},
                },
                Decreased: {
                    doshas: [],
                    percentage: {},
                    intensity: {},
                },
            },

            updateAssociateSymptom: (categoryIndex, symptomIndex, field, value) => {
                const updated = [...get().associateComplaints];
                updated[categoryIndex].symptoms[symptomIndex][field] = value;
                set({ associateComplaints: updated });
            },

            addMedicalHistory: () => {
                const updated = [...get().medicalHistory, { condition: '', hasHistory: false, duration: '' }];

                set({ medicalHistory: updated });
            },

            updateMedicalHistory: (index, field, value) => {
                const updated = [...get().medicalHistory];
                updated[index][field] = value;
                set({ medicalHistory: updated });
            },

            deleteMedicalHistory: (index) => {
                const updated = [...get().medicalHistory];
                updated.splice(index, 1);
                set({ medicalHistory: updated });
            },

            Ayurvedaparameters: Object.values(ayurvedaAssessments || {}).map(assessment => ({
                ...assessment,
                value: '',
                questions: assessment.questions.map(q => ({ ...q, answer: '' }))
            })),

            Medicineinputs: [
                {
                    medicine: "",
                    Reason: "",
                    dose: "",
                    instruction: "",
                    duration: "",
                    quantity: 1,
                    id: "",
                    timing: "",
                },
            ],

            Medicaldetails: {
                Complaints: "",
                lab: "",
                history: "",
                drugs: "",
                ayurvedadiagnosis: "",
                moderndiagnosis: "",
                diagnosis: "",
            },

            setdoshaAIdiagnois: (dataOrUpdater) =>
                set((state) => ({
                    doshaAIdiagnois:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state?.doshaAIdiagnois)
                            : dataOrUpdater,
                })),

            setFamilyHistory: (dataOrUpdater) =>
                set((state) => ({
                    familyHistory:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state?.familyHistory)
                            : dataOrUpdater,
                })),

            setMedicaldetails: (dataOrUpdater) =>
                set((state) => ({
                    Medicaldetails:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state?.Medicaldetails)
                            : dataOrUpdater,
                })),

            currentDrugs: [],

            setcurrentDrugs: (dataOrUpdater) =>
                set((state) => ({
                    currentDrugs:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.currentDrugs)
                            : dataOrUpdater,
                })),

            addCurrentdrugs: () => {
                set((state) => ({
                    currentDrugs: [...(state.currentDrugs || []), { medicine: "", dose: "", duration: "" }]
                }));
            },

            removeCurrentdrugs: (index) => {
                set((state) => {
                    const updated = [...(state.currentDrugs || [])];
                    updated.splice(index, 1);
                    return { currentDrugs: updated };
                });
            },

            updateCurrentdrugs: (index, field, value) => {
                set((state) => {
                    const updated = [...(state.currentDrugs || [])];
                    if (updated[index]) {
                        updated[index][field] = value;
                    }
                    return { currentDrugs: updated };
                });
            },

            setMedicineinputs: (dataOrUpdater) =>
                set((state) => ({
                    Medicineinputs:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.Medicineinputs)
                            : dataOrUpdater,
                })),

            setAyurvedaparameters: (dataOrUpdater) =>
                set((state) => ({
                    Ayurvedaparameters:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.Ayurvedaparameters)
                            : dataOrUpdater,
                })),

            handleAnswerSelect: (parameter, questionId, value) =>
                set((state) => {
                    const norm = (s) => (s ?? "").toString().trim().toLowerCase();

                    const updated = (state.Ayurvedaparameters ?? []).map((item) => {
                        if (norm(item?.parameter) !== norm(parameter)) return item;

                        const qs = Array.isArray(item?.questions) ? item.questions : [];
                        const updatedQuestions = qs.map((q) =>
                            q?.id === questionId ? { ...q, answer: value } : q
                        );

                        return {
                            ...item,
                            questions: updatedQuestions,
                            value: majorityFromQuestions(item?.parameter, updatedQuestions) || "",
                        };
                    });

                    return { Ayurvedaparameters: updated };
                }),

            handleEditResult: (paramObj) => {
                set({
                    isEditingResult: paramObj?.parameter ?? null,
                    editValue: paramObj?.value ?? "",
                });
            },

            setEditValue: (v) => set({ editValue: v }),

            handleSaveResult: (parameterName) => {
                const { Ayurvedaparameters, editValue } = get();
                if (!Array.isArray(Ayurvedaparameters)) {
                    return set({ isEditingResult: null, editValue: "" });
                }

                const norm = (s) => (s ?? "").toString().trim().toLowerCase();
                const targetParam = (Ayurvedaparameters.find(p => norm(p?.parameter) === norm(parameterName)) ?? {}).parameter;

                const allowed = ayurvedaFields?.[targetParam]?.options;
                const nextValue = (allowed && allowed.length && !allowed.includes(editValue))
                    ? editValue
                    : editValue;

                let changed = false;

                const updated = Ayurvedaparameters.map(p => {
                    if (norm(p?.parameter) !== norm(parameterName)) return p;
                    if ((p?.value ?? "") === nextValue) return p;
                    changed = true;
                    return { ...p, value: nextValue };
                });

                set({
                    Ayurvedaparameters: changed ? updated : Ayurvedaparameters,
                    isEditingResult: null,
                    editValue: "",
                });
            },

            cancelEditResult: () => set({ isEditingResult: null, editValue: "" }),

            setIsEditingResult: (param) => set({ isEditingResult: param }),

            handleAyurvedaAnswerSelect: (parameterName, questionId, selectedValue) => {
                set(state => {
                    const updatedParams = state.Ayurvedaparameters.map(param => {
                        if (param.parameter === parameterName) {
                            const updatedQuestions = param.questions.map(q =>
                                q.id === questionId ? { ...q, answer: selectedValue } : q
                            );

                            const answers = updatedQuestions.map(q => q.answer).filter(Boolean);
                            let dominantValue = '';
                            if (answers.length > 0) {
                                const valueCounts = answers.reduce((acc, value) => {
                                    acc[value] = (acc[value] || 0) + 1;
                                    return acc;
                                }, {});
                                dominantValue = Object.keys(valueCounts).reduce((a, b) =>
                                    valueCounts[a] > valueCounts[b] ? a : b
                                );
                            }
                            return {
                                ...param,
                                questions: updatedQuestions,
                                value: dominantValue,
                            };
                        }
                        return param;
                    });
                    return { Ayurvedaparameters: updatedParams };
                });
            },

            handleSaveAyurvedaResult: (parameterName, editValue) => {
                set(state => ({
                    Ayurvedaparameters: state.Ayurvedaparameters.map(p =>
                        p.parameter === parameterName
                            ? { ...p, value: editValue }
                            : p
                    )
                }));
            },

            setPrakritiDetails: (dataOrUpdater) =>
                set((state) => ({
                    Prakriti_details:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.Prakriti_details)
                            : dataOrUpdater,
                })),

            setDosham: (dataOrUpdater) =>
                set((state) => ({
                    Dosham:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.Dosham)
                            : dataOrUpdater,
                })),

            HandleDosham: (type, dosha, isChecked) => {
                const { setDosham } = get();

                setDosham((prev) => {

                    const section = prev?.[type];

                    if (!section || !Array.isArray(section.doshas)) {
                        console.error(`Invalid Dosham type or doshas not array for: ${type}`);
                        return prev;
                    }

                    const alreadySelected = section.doshas.includes(dosha);

                    const updatedDoshas = isChecked
                        ? alreadySelected
                            ? section.doshas
                            : [...section.doshas, dosha]
                        : section.doshas.filter((d) => d !== dosha);

                    return {
                        ...prev,
                        [type]: {
                            ...section,
                            doshas: updatedDoshas,
                        },
                    };
                });
            },


            updateDoshaPercentage: (type, dosha, value) => {
                const { setDosham } = get();
                setDosham((prev) => ({
                    ...prev,
                    [type]: {
                        ...prev[type],
                        percentage: {
                            ...prev[type].percentage,
                            [dosha]: value,
                        },
                    },
                }));
            },

            updateDoshaIntensity: (type, dosha, value) => {
                const { setDosham } = get();
                setDosham((prev) => ({
                    ...prev,
                    [type]: {
                        ...prev[type],
                        intensity: {
                            ...prev[type].intensity,
                            [dosha]: value,
                        },
                    },
                }));
            },

            setAiParameters: (dataOrUpdater) =>
                set((state) => ({
                    aiParameters:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.aiParameters)
                            : dataOrUpdater,
                })),

            setFormData: (dataOrUpdater) =>
                set((state) => ({
                    formData:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.formData)
                            : dataOrUpdater,
                })),

            setMetrics: (dataOrUpdater) =>
                set((state) => ({
                    metrics:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.metrics)
                            : dataOrUpdater,
                })),

            setCustomMetrics: (dataOrUpdater) =>
                set((state) => ({
                    customMetrics:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.customMetrics)
                            : dataOrUpdater,
                })),

            setlifestylepractices: (dataOrUpdater) => {
                set((state) => ({
                    lifestylePractices:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.lifestylePractices)
                            : dataOrUpdater,
                }));
            },

            getLifestylePracticesRecommendations: async (UID) => {
                try {
                    set({ lifestylePracticesLoading: true });

                    const { selectedPatientdetails, diseases, Dosham, Prakriti_details, symptoms } = get();

                    // Calculate age from DOB
                    const calculateAge = (dob) => {
                        if (!dob) return null;
                        const birthDate = new Date(dob);
                        const today = new Date();
                        let age = today.getFullYear() - birthDate.getFullYear();
                        const m = today.getMonth() - birthDate.getMonth();
                        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                            age--;
                        }
                        return age;
                    };

                    // Determine Prakriti Doshas from scores
                    const getPrakritiDoshas = (prakriti) => {
                        if (!prakriti) return [];
                        const doshas = [];
                        const scores = [
                            { name: "Vata", value: prakriti.vata || 0 },
                            { name: "Pitta", value: prakriti.pitta || 0 },
                            { name: "Kapha", value: prakriti.kapha || 0 }
                        ];
                        // Get doshas with significant scores (e.g., top 2 or above 30%)
                        const sorted = scores.sort((a, b) => b.value - a.value);
                        if (sorted[0].value > 0) doshas.push(sorted[0].name);
                        if (sorted[1].value > 0 && (sorted[0].value - sorted[1].value) < 15) {
                            doshas.push(sorted[1].name);
                        }
                        return doshas;
                    };

                    const payload = {
                        UID: UID || selectedPatientdetails?.UID,
                        age: selectedPatientdetails?.dob,
                        gender: selectedPatientdetails?.gender || "unknown",
                        symptoms: Array.isArray(symptoms) ? symptoms.map((e) => e.symptoms).join(',') : "",
                        disease: Array.isArray(diseases) ? diseases.join(", ") : "none",
                        vikriti_doshas: Dosham?.Increased?.doshas || [],
                        prakriti_doshas: getPrakritiDoshas(Prakriti_details)
                    };

                    console.log("Fetching lifestyle recommendations with payload:", payload);

                    const res = await profileModel.post('/getLifestyleRecommendations', payload)

                    if (res?.status === 200 && res?.data) {
                        const { analysis } = res.data;
                        // Ensure analysis is an object
                        let parsedData = (typeof analysis === 'string') ? JSON.parse(cleanJsonString(analysis)) : analysis;

                        // Normalize keys to TitleCase for UI consistency
                        const normalizedData = {};
                        if (parsedData) {
                            Object.keys(parsedData).forEach(key => {
                                const titleCaseKey = key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
                                normalizedData[titleCaseKey] = parsedData[key];
                            });
                        }

                        // Special handling for Meditation IDs
                        if (normalizedData.Meditation) {
                            normalizedData.Meditation = normalizedData.Meditation.map(practice => ({
                                ...practice,
                                Id: practice.id || practice.Id, // Ensure Id is present
                                // keep id for consistency if needed, but UI uses Id for meditation
                            }));
                        }

                        console.log("normalizedData", normalizedData);
                        set({ lifestylePractices: normalizedData });
                        console.log("Lifestyle practices fetched successfully:", normalizedData);
                    }


                } catch (error) {
                    console.log("Error fetching lifestyle practices:", error);
                } finally {
                    set({ lifestylePracticesLoading: false });
                }
            },

            getMedicineRecommendations: async () => {
                try {
                    const { selectedPatientdetails, symptoms } = get();

                    if (!symptoms || symptoms.length === 0) {
                        toast.error("Please enter chief complaints before proceeding", {
                            position: "top-center",
                            limit: 1,
                            transition: Zoom,
                            autoClose: 2000,
                            hideProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: "dark",
                        });
                        return;
                    }

                    set({ MedicineRecommendationLoading: true });

                    const res = await shopModel.post('/api/v1/getDrugFromAIWithParam', {
                        UID: selectedPatientdetails?.UID,
                        chief_complaints: symptoms.map((e) => e?.symptoms || "")
                    });

                    if (res?.status === 200 && Array.isArray(res?.data)) {
                        const data = res.data;
                        const parsedData = data.map((e) => ({
                            ...(e?.general || {}),
                            id: e?.id,
                            reason: e?.reason
                        }));
                        set({ medicinerecommendation: parsedData });
                        console.log("Medicine recommendations fetched successfully:", parsedData);
                    }

                } catch (error) {
                    console.error("Error fetching medicine recommendations:", error);
                    toast.error("Something went wrong while fetching recommendations.", {
                        position: "top-center",
                        autoClose: 2000,
                        theme: "dark",
                    });
                } finally {
                    set({ MedicineRecommendationLoading: false });
                }
            },

            getPulseDetails: async (UID) => {
                try {
                    const res = await profileModel.get(`/GetPulse/${UID}`);
                    if (res?.status === 200 && res?.data) {
                        set({ tcmPulseDiagnosis: res.data });
                        console.log("Pulse details fetched successfully:", res.data);
                    }
                } catch (error) {
                    console.error("Error fetching pulse details:", error);
                }
            },

            resetPrescriptionState: () =>
                set((state) => {
                    const { selectedPatientdetails, selectedAppointemntdetails } = state;
                    return {
                        selectedPatientdetails,
                        selectedAppointemntdetails,
                        audioBase64: null,
                        selectedPrescription: null,
                        openRecipeAI: false,
                        recipeLoading: false,
                        recipeAIRecommendation: [],
                        selectedRecipes: [],
                        metrics: {
                            bloodPressure: "",
                            randomBloodSugar: "",
                            pulseRate: "",
                            oximeterValue: "",
                            gripStrength: "",
                        },
                        formData: {},
                        customMetrics: [],
                        labTests: [],
                        FoodtobeTaken: [],
                        Foodtobeavoided: [],
                        yogalist: {},
                        meditationList: {},
                        pranayama: {},
                        lifestylechanges: "",
                        FollowUp: "No",
                        symptoms: [],
                                    diseases: [],
                        complications: [],
                        habits: [],
                        lifestylePractices: [],
                        Prakriti_details: null,
                        lifestylePracticesLoading: false,
                        aiParameters: [],
                        doshaAIdiagnois: null,
                        patientProfile: null,
                        herbalProducts: [],
                        AllDiet: {},
                        AllrawDiet: [],
                        AllRecipes: [],
                        Recipes: [],
                        Practices: [],
                        transcript: "",
                        error: null,
                        includedCategories: [],
                        avoidedCategories: [],
                        creationStartTime: null,

                        Dosham: {
                            Increased: { doshas: [], percentage: {}, intensity: {} },
                            Decreased: { doshas: [], percentage: {}, intensity: {} },
                            Reason: ""
                        },

                        Ayurvedaparameters: Object.values(ayurvedaAssessments || {}).map(assessment => ({
                            ...assessment,
                            value: '',
                            questions: assessment.questions.map(q => ({ ...q, answer: '' }))
                        })),

                        currentDrugs: [],

                        Medicineinputs: [
                            {
                                medicine: "",
                                Reason: "",
                                dose: "",
                                instruction: "",
                                duration: "",
                                quantity: 1,
                                id: "",
                                timing: "",
                            },
                        ],

                        Medicaldetails: {
                            Complaints: "",
                            lab: "",
                            history: "",
                            drugs: "",
                            ayurvedadiagnosis: "",
                            moderndiagnosis: "",
                            diagnosis: "",
                        },

                        associateComplaints: [
                            {
                                category: "Digestive Disorders",
                                dhatu: "Rasa",
                                symptoms: [
                                    { symptoms: "Indigestion", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "IBS", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Acidity", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Gastritis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Ulcer", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "GERD", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                            {
                                category: "Hematological Disorders",
                                dhatu: "Rakta",
                                symptoms: [
                                    { symptoms: "Anemia", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Cholesterol", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Hypertension", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Triglyceride", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Varicose vein", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                            {
                                category: "Muscular Disorders",
                                dhatu: "Mamsa",
                                symptoms: [
                                    { symptoms: "Obesity", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Muscle pain", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Weight Loss", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Debility", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                            {
                                category: "Skin Disorders",
                                dhatu: "Medas",
                                symptoms: [
                                    { symptoms: "Itching", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Vitiligo", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Discolouration", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Eczema", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Psoriasis", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                            {
                                category: "Skeletal Disorders",
                                dhatu: "Asthi",
                                symptoms: [
                                    { symptoms: "Osteo Arthritis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Disc prolapse", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Sciatica", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Rheumatoid arthritis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Lumbar Spondylosis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Cervical Spondylosis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Gout", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                            {
                                category: "Nervous Disorders",
                                dhatu: "Majja",
                                symptoms: [
                                    { symptoms: "Parkinson's disease", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Alzheimer's disease", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Paralysis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Immunity", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                            {
                                category: "Anorectal Disorders",
                                dhatu: "Mooladhara",
                                symptoms: [
                                    { symptoms: "Piles", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Fissure in ano", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Fistula in ano", severity: 0, duration: "", frequency: "", goal: false },

                                ],
                            },
                            {
                                category: "Reproductive System Disorders",
                                dhatu: "Swadhisthana",
                                symptoms: [
                                    { symptoms: "Male Infertility", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Prostate Disorder", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Erectile dysfunction", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Female infertility", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Fibroid", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Amenorrhoea", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Irregular menses", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Leucorrhoea", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Dysmenorrhoea", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "PCOD", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Endometriosis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Monopausal Syndrome", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                            {
                                category: "Kidney Disorders",
                                dhatu: "Manipura",
                                symptoms: [
                                    { symptoms: "Kidney stone", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Diabetes Mellitus", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Kidney failure", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Diabetes neuritis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Diabetes nephritis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Diabetic foot ulcer", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                            {
                                category: "Cardiac / Hepatic Disorders",
                                dhatu: "Anahata",
                                symptoms: [
                                    { symptoms: "Angina Pectoris", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Cardiomyopathy", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Liver Cirrhosis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Fatty liver", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Coronary Artery diseases", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Atherosclerosis", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                            {
                                category: "Neck & thoracic related Disorders",
                                dhatu: "Vishuddhi",
                                symptoms: [
                                    { symptoms: "Thyroid", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Asthma", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Wheezing", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                            {
                                category: "Head & ENT Disorders",
                                dhatu: "Ajna",
                                symptoms: [
                                    { symptoms: "ENT", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Anxiety", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Hair Loss", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Sinusitis", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Stress", severity: 0, duration: "", frequency: "", goal: false },
                                    { symptoms: "Migraine", severity: 0, duration: "", frequency: "", goal: false },
                                ],
                            },
                        ],

                        medicalHistory: [
                            { condition: "Hypertension", hasHistory: false, duration: "" },
                            { condition: "Diabetes", hasHistory: false, duration: "" },
                            { condition: "Cardiac Issues", hasHistory: false, duration: "" },
                            { condition: "Thyroid", hasHistory: false, duration: "" }
                        ],

                        addMedicalHistory: () => {
                            const updated = [...get().medicalHistory, { condition: '', hasHistory: false, duration: '' }];

                            set({ medicalHistory: updated });
                        },

                        painHistory: {
                            pattern: "",
                            siteAndSide: "",
                            onset: "",
                            typeOfPain: "",
                            irritability: "",
                            rhythms: "",
                            aggravatingFactors: "",
                            relievingFactors: "",
                            painScore: "",
                            effectOnActivity: ""
                        },

                        painTypeOptions: [
                            { label: "Prick", selected: false, duration: "" },
                            { label: "Dull Ache", selected: false, duration: "" },
                            { label: "Burning", selected: false, duration: "" },
                            { label: "Throbbing", selected: false, duration: "" },
                            { label: "Pulling", selected: false, duration: "" },
                            { label: "Sharp Shooting", selected: false, duration: "" }
                        ],

                        painActivityEffects: [
                            { activity: "Getting in/out of Bed", affected: false, duration: "" },
                            { activity: "Walking up / down stairs", affected: false, duration: "" },
                            { activity: "Getting in/out of chair", affected: false, duration: "" },
                            { activity: "Standing / Walking", affected: false, duration: "" },
                            { activity: "Working activities", affected: false, duration: "" },
                            { activity: "Other activities (sitting, cooking, cleaning, lifting etc.)", affected: false, duration: "" },
                            { activity: "Dysmenorrhoea", affected: false, duration: "" }
                        ],

                        generalExamination: {
                            generalBodyBuilt: "",
                            gait: "",
                            posture: "",
                            duration: "",
                        },

                        localExamination: {
                            temperature: "",
                            spasm: "",
                            tenderness: "",
                            otherFindings: "",
                            duration: "",
                            swelling: "",
                            crepitus: ""
                        },

                        rangeOfMotion: "",

                        labInvestigation: "",

                        otherJoints: {
                            tightness: null,
                            contracture: null,
                            deformity: null,
                        },

                        marmaPoints: [
                            {
                                point: "",
                                location: "",
                                painLevel: 0,
                            },
                        ],

                        kosha: "",

                        irisDiagnosis: "",

                        treatmentPlan: "",

                        homeProgram: "",

                        ergonomicsAdvice: "",

                        dosha: "",

                        dhatu: '',

                        chakra: "",

                        surgicalHistory: [],

                        socioEconomicStatus: { status: "" },

                        obgynHistory: {
                            lmp: "",
                            menstrualCycle: "",
                            menstrualFlow: "",
                            dysmenorrhoea: null,
                            leucorrhoea: null,
                            delivery: ""
                        },

                        tcmPulseDiagnosis: {
                            left: {
                                heart: "", liver: "", kidney: "", urinaryBladder: "", smallIntestine: "", gallBladder: ""
                            },
                            right: {
                                lung: "", spleen: "", stomach: "", kidney: "", largeIntestine: "", urinaryBladder: ""
                            }
                        },

                        familyHistory: [
                            { relation: 'Father', disease: '', duration: '' },
                            { relation: 'Mother', disease: '', duration: '' }
                        ],

                    };
                }),

            setLabTests: (dataOrUpdater) =>
                set((state) => ({
                    labTests:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.labTests)
                            : dataOrUpdater,
                })),

            setFoodtobetaken: (dataOrUpdater) =>
                set((state) => ({
                    FoodtobeTaken:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.FoodtobeTaken)
                            : dataOrUpdater,
                })),

            setFoodtobeAvoided: (dataOrUpdater) =>
                set((state) => ({
                    Foodtobeavoided:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.Foodtobeavoided)
                            : dataOrUpdater,
                })),

            setyogalist: (obj) => set({ yogalist: obj }),

            setlifestylechanges: (data) => set({ lifestylechanges: data }),

            setfollowup: (data) => set({ FollowUp: data }),

            setMeditationlist: (obj) => set({ meditationList: obj }),

            setSymptoms: (dataOrUpdater) =>
                set((state) => ({
                    symptoms:
                        typeof dataOrUpdater === 'function'
                            ? dataOrUpdater(state.symptoms)
                            : dataOrUpdater,
                })),

            setDiseases: (dataOrUpdater) =>
                set((state) => ({
                    diseases:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.diseases)
                            : dataOrUpdater,
                })),

            setComplications: (dataOrUpdater) =>
                set((state) => ({
                    complications:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.complications)
                            : dataOrUpdater,
                })),

            setHabits: (dataOrUpdater) =>
                set((state) => ({
                    habits:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.habits)
                            : dataOrUpdater,
                })),

            setMedicineinputs: (dataOrUpdater) =>
                set((state) => ({
                    Medicineinputs:
                        typeof dataOrUpdater === "function"
                            ? dataOrUpdater(state.Medicineinputs)
                            : dataOrUpdater,
                })),

            setPranayamaList: (obj) => set({ pranayama: obj }),

            setRecipeAIRecommendation: (data) =>
                set((state) => {
                    const timings = ["breakfast", "lunch", "snack", "dinner"];
                    const currentRecommendations = state.recipeAIRecommendation || [];

                    const hasAllTimings = timings.every(t =>
                        currentRecommendations.some(r => r.time === t)
                    );

                    return {
                        recipeAIRecommendation: hasAllTimings
                            ? [...data]
                            : [...currentRecommendations, ...data],
                    };
                }),

            setSelectedRecipes: (data) => set({ selectedRecipes: data }),

            handleSelectRecipe: (item, isChecked) => {
                const current = get().selectedRecipes;
                const updated = isChecked
                    ? [...current, item]
                    : current.filter((i) => i.id !== item.id);
                set({ selectedRecipes: updated });
            },

            setOpenRecipeAI: (val) => set({ openRecipeAI: val }),

            setRecipeLoading: (val) => set({ recipeLoading: val }),

            setAllDiet: (data) => set({ AllDiet: data }),
            setAllrawDiet: (data) => set({ AllrawDiet: data }),
            setAllRecipes: (data) => set({ AllRecipes: data }),
            setRecipes: (data) => set({ Recipes: data }),
            setPractices: (data) => set({ Practices: data }),
            setTranscript: (data) => set({ transcript: data }),
            setError: (data) => set({ error: data }),
            setincludedCategories: (data) => set({ includedCategories: data }),
            setavoidedCategories: (data) => set({ avoidedCategories: data }),
            setCreationStartTime: (data) => set({ creationStartTime: data }),
            setlifestylePracticesLoading: (data) => set({ lifestylePracticesLoading: data }),
            setMedicineRecommendationLoading: (data) => set({ MedicineRecommendationLoading: data }),
            setPatientProfile: (data) => set({ patientProfile: data }),
            setHerbalProducts: (data) => set({ herbalProducts: data }),
            handleCancelResult: () => {},

        }),
        {
            name: 'Prescription',
            getStorage: () => sessionStorage,
        }
    )
);

export default usePrescriptionStore;